import { Sprite } from "pixi.js";
import screenShakeComponent from "slotworks/components/effects/screen-shake-component";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import { AbstractSceneTransition } from "slotworks/graphics/layers/scene-transitions/abstract-scene-transition";
import settingsModel from "slotworks/model/settings/settings-model";
import { Services } from "slotworks/services/services";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Point from "slotworks/utils/geom/point";
import { Tween } from "slotworks/utils/tween";
import { Easing } from "slotworks/utils/tween";
import Sequence from "slotworks/utils/contracts/sequence";
import { MultiStyleText } from "slotworks/graphics/pixi/pixi-multistyle-text";
import textStylePatch from "utils/text-style-patch";

const SCENE = {
    BIG: "bigWin",
    MEGA: "megaBigWin",
    HUGE: "hugeBigWin"
};

const SHAKE_DURATION: number = 2000;
const LABEL_SCALE_DURATION: number = 400;


type BigWinAnimationSteps = {
    in: string, loop: string, out: string;
};

export default class BigWinTextTransition extends AbstractSceneTransition {

    private scene: string;

    private labels: PIXI.Text[];
    private valueText: PIXI.Text;
    private spineAnimation: PIXI.spine.Spine;

    private spineId: string = "big_win_spine";

    private labelIds: { [key: string]: string[]; } = {
        [SCENE.BIG]: ["bigWin_text"],
        [SCENE.MEGA]: ["bigWin_text", "megaBigWin_text"],
        [SCENE.HUGE]: ["bigWin_text", "megaBigWin_text", "hugeBigWin_text"]
    };

    private spineAnimationIds: Map<string, BigWinAnimationSteps> = new Map<string, BigWinAnimationSteps>([
        [SCENE.BIG, { in: "big_win_in", loop: "big_win_loop", out: "big_win_out" }],
        [SCENE.MEGA, { in: "big_win_to_mega", loop: "mega_win_loop", out: "mega_win_out" }],
        [SCENE.HUGE, { in: "mega_to_huge", loop: "huge_win_loop", out: "huge_win_out" }]
    ] as any);

    private soundIds: { [key: string]: string; } = {
        [SCENE.BIG]: "big_win",
        [SCENE.MEGA]: "mega_big_win",
        [SCENE.HUGE]: "huge_mega_big_win"
    };

    constructor(layer: Layers, scene: string) {
        super(layer);
        this.scene = scene;

        canvasManager.onOrientationChange.add(() => this.repositionValue());
    }

    public in(): Contract<void> {
        this.spineAnimation = Layers.CelebrationBackground.getSpine(this.spineId);
        this.valueText = Layers.CelebrationForeground.getText("value");
        this.labels = this.labelIds[this.scene].map(id => Layers.CelebrationContent.getText(id));
        if (this.isEnglishVersion()) {
            return this.playEnglishVersion();
        } else {
            return this.playSimpleVersion();
        }
    }

    private playEnglishVersion(): Contract<void> {
        this.labels.forEach(label => label.visible = false);
        this.valueText.visible = true;
        this.repositionValue();
        screenShakeComponent.shakeDuration = SHAKE_DURATION;

        Services.sound.customEvent(this.soundIds[this.scene]);

        let labelScaleFrom = this.scene === SCENE.BIG ? new Point(0, 0) : new Point(1, 1);

        return new Parallel([
            () => screenShakeComponent.shakeScreen(),
            () => this.scaleFromTo(this.valueText, labelScaleFrom, new Point(1, 1), LABEL_SCALE_DURATION),
            () => new Sequence([
                () => this.playSpineAnimation(this.spineAnimationIds.get(this.scene).in),
                () => Contract.wrap(() => {
                    this.spineAnimation.state.clearListeners();
                    this.spineAnimation.state.setAnimation(0, this.spineAnimationIds.get(this.scene).loop, true);
                })
            ])
        ]);
    }

    private playSpineAnimation(id: string): Contract<void> {
        this.spineAnimation.state.clearListeners();
        this.spineAnimation.state.setAnimation(0, id, false);

        return new Contract<void>((resolve) => {
            this.spineAnimation.state.addListener({
                complete: () => {
                    this.spineAnimation.state.clearListeners();
                    resolve();
                }
            });
        });
    }

    private playSimpleVersion(): Contract<void> {

        this.updateSimpleVersionValueTextStyle();

        textStylePatch.applyCelebrationTextStyle(...this.labels, this.valueText);
        
        this.spineAnimation.state.clearTracks();
        this.labels.forEach(label => label.visible = true);
        this.spineAnimation.visible = false;
        this.valueText.visible = true;
        this.repositionValue();

        screenShakeComponent.shakeDuration = SHAKE_DURATION;

        Services.sound.customEvent(this.soundIds[this.scene]);

        switch (this.scene) {
            case SCENE.BIG:
                return new Parallel([
                    () => screenShakeComponent.shakeScreen(),
                    () => this.scaleFromTo(this.labels[0], new Point(0, 0), new Point(1, 1), 250),
                    () => this.scaleFromTo(this.valueText, new Point(0, 0), new Point(1, 1), 250)
                ]);
            case SCENE.MEGA:
                return new Parallel([
                    () => screenShakeComponent.shakeScreen(),
                    () => this.landIn(this.valueText, 25, 50),
                    () => this.landIn(this.labels[0], 50, 100),
                    () => this.landIn(this.labels[1], 75, 150)
                ]);
            case SCENE.HUGE:
                return new Parallel([
                    () => screenShakeComponent.shakeScreen(),
                    () => this.landIn(this.valueText, 25, 50),
                    () => this.landIn(this.labels[0], 50, 100),
                    () => this.landIn(this.labels[1], 75, 150),
                    () => this.landIn(this.labels[2], 100, 200)
                ]);
        }
    }


    private updateSimpleVersionValueTextStyle(): void {
        let textStyle = (this.labels[0] as MultiStyleText).getTagStyle("default");
        (this.valueText as MultiStyleText).styles = { default: textStyle };
        (this.valueText as MultiStyleText).updateText();
    }

    private isEnglishVersion(): boolean {
        return settingsModel.language === "en";
    }

    public out(): Contract<void> {
        if (this.isEnglishVersion()) {
            return new Parallel([
                () => new Sequence([
                    () => this.playSpineAnimation(this.spineAnimationIds.get(this.scene).out),
                    () => Contract.getTimeoutContract(0),
                ]),
                () => this.scaleFromTo(this.valueText, new Point(1, 1), new Point(0, 0), 300)
            ]);

        } else {
            return new Parallel([
                () => this.scaleFromTo(this.labels[0], new Point(1, 1), new Point(0, 0), 300),
                () => this.scaleFromTo(this.labels[1], new Point(1, 1), new Point(0, 0), 300),
                () => this.scaleFromTo(this.labels[2], new Point(1, 1), new Point(0, 0), 300),
                () => this.scaleFromTo(this.valueText, new Point(1, 1), new Point(0, 0), 300)
            ]);
        }
    }

    public destroy(): void {
        this.valueText = null;
        super.destroy();
    }


    private repositionValue(): void {
        if (this.valueText) {
            let layoutOffsetY: number = 0;
            let animationOffsetY: number = 0;

            if (this.isEnglishVersion()) {
                if (this.scene === SCENE.BIG || this.scene === SCENE.MEGA) {
                    animationOffsetY = 640;
                }
                if (this.scene === SCENE.HUGE) {
                    animationOffsetY = 670;
                }
            } else {
                if (this.scene === SCENE.BIG) {
                    animationOffsetY = 460;
                }
                if (this.scene === SCENE.MEGA) {
                    animationOffsetY = 580;
                }
                if (this.scene === SCENE.HUGE) {
                    animationOffsetY = 660;
                }
            }

            this.valueText.y = this.spineAnimation.y + animationOffsetY + layoutOffsetY;
        }
    }


    private scaleFromTo(elem: PIXI.Text | Sprite, scaleFrom: Point, scaleTo: Point, duration: number): Contract<void> {
        if (!elem) {
            return Contract.empty();
        }

        return new Contract<void>((resolve) => {
            elem.scale.set(scaleFrom.x, scaleFrom.y);
            const tween = new Tween(elem.scale);
            tween.to({ x: scaleTo.x, y: scaleTo.y }, duration);
            tween.easing(Easing.Exponential.InOut);
            tween.onComplete(resolve);
            tween.start();
        });
    }

    private landIn(elem: PIXI.Text | Sprite, dropDistance: number, duration: number): Contract<void> {
        const targetPos: number = elem.position.y;
        return new Contract<void>((resolve) => {
            elem.position.y = targetPos - dropDistance;
            const tween = new Tween(elem.position);
            tween.to({ y: targetPos }, duration);
            tween.easing(Easing.Exponential.Out);
            tween.onComplete(resolve);
            tween.start();
        });
    }

    private setParticleFrequency(frequency: number): Contract<void> {

        const emitter1 = Layers.CelebrationBackground.getParticle("big_win_coin_1");
        const emitter2 = Layers.CelebrationBackground.getParticle("big_win_coin_2");

        if (emitter1) {
            emitter1.frequency = frequency;
        }

        if (emitter2) {
            emitter2.frequency = frequency;
        }

        return Contract.empty();
    }
}
